// src/components/RoleBasedRoadmap.jsx
import React from "react";
import styled from "styled-components";
import {
  FaDatabase,
  FaMobileAlt,
  FaLayerGroup,
  FaServer,
  FaReact,
  FaCloud,
  FaBrain,
  FaCheckCircle,
} from "react-icons/fa";
import { gradients } from "./theme/theme";

const Section = styled.section`
  padding: 6rem 2rem;
  background: ${gradients.heroBackground};
  color: #fff;
  text-align: center;
`;

const Title = styled.h2`
  font-size: 3rem;
  margin-bottom: 3rem;
  font-weight: 700;
  background: linear-gradient(90deg, #ff7a18, #ff0066);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: auto;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  border-radius: 18px;
  padding: 2rem;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.12);
  transition: 0.3s;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 10px 25px rgba(255, 122, 24, 0.4);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.6rem;
  margin-bottom: 1rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const TechList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TechItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0.6rem 0;
  font-size: 0.95rem;
  color: #ccc;

  svg {
    color: #3cff0b; /* bright green tick */
    flex-shrink: 0;
  }
`;

export default function RoleBasedRoadmap() {
  const profiles = [
    {
      title: "Frontend Developer",
      icon: <FaReact color="#00d4ff" />,
      roadmap: [
        "HTML, CSS, JavaScript (ES6+)",
        "React.js (Hooks, Context, Router)",
        "UI Frameworks (Tailwind CSS, Material UI)",
        "State Management (Redux, Zustand)",
        "APIs & Integration (REST, GraphQL)",
        "Next.js for SSR & SEO",
      ],
    },
    {
      title: "Backend Developer",
      icon: <FaServer color="#ff7a18" />,
      roadmap: [
        "Programming Basics (JS, Python, or Java)",
        "Databases (SQL, MongoDB, PostgreSQL)",
        "Backend Frameworks (Node.js, Express, GraphQL)",
        "Authentication & Security (JWT, OAuth, APIs)",
        "Cloud & Deployment (Docker, Firebase, AWS)",
      ],
    },
    {
      title: "Mobile Developer",
      icon: <FaMobileAlt color="#00ff99" />,
      roadmap: [
        "Programming Basics (JavaScript, TypeScript)",
        "React Native (Core Components, Navigation)",
        "State Management (Redux, Context API)",
        "APIs & Storage (REST, GraphQL, SQLite, Firebase)",
        "Native Features (Push Notifications, Maps, Camera)",
      ],
    },
    {
      title: "Full-Stack Developer",
      icon: <FaLayerGroup color="#ff00cc" />,
      roadmap: [
        "Frontend (HTML, CSS, JavaScript, React)",
        "Backend (Node.js, Express, GraphQL)",
        "Databases (SQL, MongoDB, Firebase)",
        "Mobile (React Native basics)",
        "DevOps Basics (GitHub, CI/CD, Cloud Hosting)",
      ],
    },
    {
      title: "DevOps / Cloud Engineer",
      icon: <FaCloud color="#ffaa00" />,
      roadmap: [
        "Linux & Networking Basics",
        "Version Control (Git, GitHub)",
        "CI/CD Pipelines (GitHub Actions, Jenkins)",
        "Docker & Kubernetes",
        "Cloud Platforms (AWS, GCP, Azure)",
        "Monitoring & Scaling (Prometheus, Grafana)",
      ],
    },
    {
      title: "AI / Data Engineer",
      icon: <FaBrain color="#9d4edd" />,
      roadmap: [
        "Python Programming & Math Basics",
        "Data Handling (NumPy, Pandas, SQL)",
        "Machine Learning (Scikit-learn, TensorFlow, PyTorch)",
        "APIs for AI Models (Flask, FastAPI)",
        "Cloud AI Services (Google AI, AWS SageMaker)",
        "Deploy ML Models (Docker, Streamlit, TF.js)",
      ],
    },
  ];

  return (
    <Section>
      <Title>🎯 Roadmaps by Career Path</Title>
      <Grid>
        {profiles.map((profile, idx) => (
          <Card key={idx}>
            <CardTitle>
              {profile.icon} {profile.title}
            </CardTitle>
            <TechList>
              {profile.roadmap.map((tech, i) => (
                <TechItem key={i}>
                  <FaCheckCircle /> {tech}
                </TechItem>
              ))}
            </TechList>
          </Card>
        ))}
      </Grid>
    </Section>
  );
}
